import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import axios from "axios";
import {jwtDecode} from "jwt-decode"; // ✅ NEW: For decoding JWT
import { useNavigate } from "react-router-dom"; // ✅ NEW: For redirecting
import { useParams } from "react-router-dom";

// Tenant data shape
interface TenantData {
  tenant_id: number;
  tenant_slug: string;
  tenant_name: string;
  logo_url: string;
  contact_email: string;            // NEW
  contact_phone?: string | null;    // NEW
  support_email?: string | null;    // NEW
}

interface TenantContextType {
  tenant: TenantData | null;
  loading: boolean;
  error: boolean;
  setTenant: (tenant: TenantData | null) => void;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  loading: true,
  error: false,
  setTenant: () => {},
});

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantContextProvider");
  }
  return context;
};

interface TenantContextProviderProps {
  children: ReactNode;
}

// JWT decoding helper
const extractTenantIdFromToken = (token: string): number | null => {
  try {
    const decoded: any = jwtDecode(token);
    return decoded.tenant_id || null;
  } catch {
    return null;
  }
};

export const TenantContextProvider: React.FC<TenantContextProviderProps> = ({ children }) => {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const navigate = useNavigate();


  const clearTenant = () => {
    setTenant(null);
    setError(false);
    setLoading(false);
    localStorage.removeItem("tenantSlug");
  };



  useEffect(() => {
  if (tenant) {
    console.log("Tenant context updated:", tenant);
  }
}, [tenant]);

  const fetchTenantData = async (urlSlug: string) => {
    setLoading(true);
    setError(false);

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const tokenTenantId = token ? extractTenantIdFromToken(token) : null;

    try {
      if (tokenTenantId) {
        // Always trust token first
        const response = await axios.get(`${apiUrl}/tenant/${tokenTenantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const actualTenant = response.data;

        // If the URL slug is wrong, silently fix it via redirect
        if (actualTenant.slug !== urlSlug) {
          console.warn("URL slug mismatch with token. Redirecting...");
          navigate(`/platform/${actualTenant.slug}/app`, { replace: true });
          return;
        }

      // inside the tokenTenantId branch
      const tenantData: TenantData = {
        tenant_id: actualTenant.tenant_id,
        tenant_slug: actualTenant.slug,
        tenant_name: actualTenant.name,
        logo_url: actualTenant.logo_image_url,
        contact_email: actualTenant.contact_email,       // NEW
        contact_phone: actualTenant.contact_phone,        // NEW
        support_email: actualTenant.support_email,        // NEW
      };

        localStorage.setItem("tenantSlug", tenantData.tenant_slug);
        setTenant(tenantData);
        return;
      }

      // If not logged in, rely on slug-based fetch
      const res = await axios.get(`${apiUrl}/public/tenant/by-slug/${urlSlug}`);
      // inside the public/by-slug branch
      const tenantData: TenantData = {
        tenant_id: res.data.tenant_id,
        tenant_slug: res.data.tenant_slug,
        tenant_name: res.data.tenant_name,
        logo_url: res.data.logo_url,
        contact_email: res.data.contact_email,       // NEW
        contact_phone: res.data.contact_phone,        // NEW
        support_email: res.data.support_email,        // NEW
      };

      localStorage.setItem("tenantSlug", tenantData.tenant_slug);
      setTenant(tenantData);
    } catch (err) {
      console.error("Tenant fetch failed:", err);
      clearTenant();
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if (!tenantSlug) {
      clearTenant();
      return;
    }

    // If already loaded for this slug, skip
    if (tenant && tenant.tenant_slug === tenantSlug) {
      setLoading(false);
      return;
    }

    fetchTenantData(tenantSlug);
  }, [tenantSlug , tenant?.tenant_slug]);

  return (
    <TenantContext.Provider value={{ tenant, loading, error, setTenant }}>
      {children}
    </TenantContext.Provider>
  );
};
